import { useState } from "react";
import CalculatorLayout from "@/components/CalculatorLayout";
import { ResultCard } from "@/components/ResultCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { addDays, formatDate } from "@/calculators/_helpers";

export function AgeCalculator({ calc }) {
  const [gender, setGender] = useState("male");
  const [dob, setDob] = useState("");
  const [target, setTarget] = useState(new Date().toISOString().slice(0, 10));

  const parse = (s) => (s ? new Date(s + "T00:00:00") : null);
  const d1 = parse(dob);
  const d2 = parse(target);
  let result = null;

  if (d1 && d2 && !isNaN(d1) && !isNaN(d2) && d2 >= d1) {
    let years = d2.getFullYear() - d1.getFullYear();
    let months = d2.getMonth() - d1.getMonth();
    let days = d2.getDate() - d1.getDate();
    if (days < 0) {
      months -= 1;
      const prev = new Date(d2.getFullYear(), d2.getMonth(), 0);
      days += prev.getDate();
    }
    if (months < 0) { years -= 1; months += 12; }

    const nextBd = new Date(d2.getFullYear(), d1.getMonth(), d1.getDate());
    if (nextBd < d2) nextBd.setFullYear(nextBd.getFullYear() + 1);
    const daysToBd = Math.ceil((nextBd - d2) / (1000 * 60 * 60 * 24));
    const totalDays = Math.floor((d2 - d1) / (1000 * 60 * 60 * 24));

    result = { years, months, days, daysToBd, totalDays, nextBd };
  }

  return (
    <CalculatorLayout
      calc={calc}
      intro="Find your exact age in years, months and days — plus how long until your next birthday."
      resultText={result && `Age: ${result.years}y ${result.months}m ${result.days}d — ${result.daysToBd} days until next birthday.`}
      formula={<p>Age is calculated as the difference between your date of birth and the target date, broken down into years, months and days. Total days is the raw day-count between the two dates.</p>}
      faqs={[
        { q: "Does the calculator count leap years?", a: "Yes — because we compare real calendar dates, leap days are naturally included." },
        { q: "Can I calculate age at a past or future date?", a: "Yes, change the 'Target date' field to any date to see age on that day." },
        { q: "Why is 'days until next birthday' 0?", a: "That means the target date is your birthday." },
      ]}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Gender</Label>
          <RadioGroup value={gender} onValueChange={setGender} className="flex gap-2" data-testid="gender-toggle">
            {["male", "female"].map((g) => (
              <div key={g} className="flex-1">
                <RadioGroupItem value={g} id={`age-g-${g}`} className="peer sr-only" />
                <Label
                  htmlFor={`age-g-${g}`}
                  className="flex h-10 cursor-pointer items-center justify-center rounded-full border border-border text-sm font-medium capitalize peer-data-[state=checked]:bg-primary peer-data-[state=checked]:text-primary-foreground peer-data-[state=checked]:border-primary"
                >
                  {g}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="dob">Date of birth</Label>
            <Input id="dob" type="date" data-testid="age-dob" value={dob} onChange={(e) => setDob(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="target">Target date</Label>
            <Input id="target" type="date" data-testid="age-target" value={target} onChange={(e) => setTarget(e.target.value)} />
          </div>
        </div>
      </div>

      {result && (
        <div className="mt-6 space-y-4">
          <ResultCard
            label="Your age"
            value={`${result.years}y ${result.months}m ${result.days}d`}
            testId="age-result"
          >
            Total {result.totalDays.toLocaleString()} days lived.
          </ResultCard>
          <div className="grid gap-3 sm:grid-cols-2 text-sm">
            <div className="rounded-xl border border-border p-4">
              <div className="text-muted-foreground text-xs uppercase">Next birthday</div>
              <div className="mt-1 font-medium">{formatDate(result.nextBd)}</div>
            </div>
            <div className="rounded-xl border border-border p-4">
              <div className="text-muted-foreground text-xs uppercase">Days until next birthday</div>
              <div className="mt-1 font-medium">{result.daysToBd} days</div>
            </div>
          </div>
        </div>
      )}
    </CalculatorLayout>
  );
}
